import { useCallback, useState } from "react";
import Facebook from "@/assets/images/socials/facebook.svg";
import Instagram from "@/assets/images/socials/instagram.svg";
import LinkedIn from "@/assets/images/socials/linkedin.svg";
import X from "@/assets/images/socials/x.svg";
import Xing from "@/assets/images/socials/xing.svg";
import { capitalizeFirstLetter } from "@/utils/helper.util";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { CompanyDetailQueryProps } from "@/hooks/api/company/useCompanyDetail";
import { useCompanyDetailSocials } from "@/hooks/api/company/useCompanyDetailSocials";
import Badge from "@/components/atoms/Badge";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import CompanyLogo from "@/components/molecules/company/CompanyLogo";
import CompanyDetailMoreData from "@/components/molecules/company/detail/overview/basic/CompanyDetailMoreData";
import Modal from "@/components/molecules/Modal";
import HeadcountRangeBadge from "@/components/molecules/table/search/badges/HeadcountRangeBadge";
import RevenueRangeBadge from "@/components/molecules/table/search/badges/RevenueRangeBadge";
import StatusBadge from "@/components/molecules/table/search/badges/StatusBadge";

const INDUSTRIES_LIMIT = 5;

const CompanyDetailHeader = ({ company }: CompanyDetailQueryProps) => {
  const { t } = useTranslation();
  const { data } = company;

  const [moreIndustriesShown, setMoreIndustriesShown] = useState(false);

  return (
    <div className={"px-7 py-6"}>
      <div className={"mb-4 flex items-start gap-2"}>
        {!data ? (
          <>
            <div className={"size-6 animate-pulse rounded-full bg-gray-300"} />
            <div className={"mt-0.5 h-5 w-48 animate-pulse rounded-md bg-gray-300"} />
          </>
        ) : (
          <>
            <CompanyLogo url={data.logo_url} size={"md"} />
            <h2 className={"mt-0.5 pr-6 text-xl font-semibold text-primary"}>{data.name}</h2>
          </>
        )}
        <CompanyDetailSocials company={company} />
      </div>

      <div className={"flex w-2/3 flex-wrap gap-2"}>
        {!data ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className={clsx("h-5 animate-pulse rounded-md bg-gray-300", index % 2 === 0 ? "w-32" : "w-20")}
            />
          ))
        ) : (
          <>
            {data.headcount_range && (
              <HeadcountRangeBadge showTitle from={data.headcount_range.from} to={data.headcount_range.to} />
            )}
            {data.revenue_range?.range && (
              <RevenueRangeBadge
                showTitle
                from={data.revenue_range.range.from}
                to={data.revenue_range.range.to}
                year={data.revenue_range.year}
              />
            )}
            <StatusBadge status={data.status} />
            {(moreIndustriesShown ? data.industries : data.industries.slice(0, INDUSTRIES_LIMIT)).map((industry) => (
              <Badge key={industry} size="sm" label={industry} color="natural" />
            ))}
          </>
        )}
      </div>
      {data?.industries && data.industries.length > INDUSTRIES_LIMIT && (
        <Button
          onClick={() => setMoreIndustriesShown((prev) => !prev)}
          variants={"textLight"}
          color={"natural"}
          size={"2xs"}
          className={"!px-0.5"}
          label={moreIndustriesShown ? t("company.detail.lessIndustries") : t("company.detail.moreIndustries")}
        />
      )}
    </div>
  );
};

type SocialProps = {
  platform: string;
  count: number;
};

const CompanyDetailSocials = ({ company }: CompanyDetailQueryProps) => {
  const { t } = useTranslation();
  const { data } = company;
  const [activeMoreSocialType, setActiveMoreSocialType] = useState<SocialProps | null>(null);

  const imageUrl = useCallback((type: string) => {
    switch (type.toLowerCase()) {
      case "facebook":
        return Facebook;
      case "linkedin":
        return LinkedIn;
      case "instagram":
        return Instagram;
      case "twitter":
      case "x":
        return X;
      case "xing":
        return Xing;
      default:
        return null;
    }
  }, []);

  return data ? (
    data.social_links?.length > 0 && (
      <>
        <div className={"ml-auto flex shrink-0 justify-end gap-2"}>
          {data.social_links.map((social) => {
            const image = imageUrl(social.platform)!;

            return imageUrl(social.platform) ? (
              <div className={clsx("relative", social.count > 1 && "pr-6")}>
                <a key={social.url} href={social.url} target="_blank" rel="noreferrer">
                  <img src={image} className={"size-6"} alt={social.platform} />
                </a>
                {social.count > 1 && (
                  <div className={"absolute left-4.5 top-0"}>
                    <Button
                      label={`+${Math.min(social.count - 1, 99)}`}
                      color="natural"
                      variants={"outline"}
                      size={"2xs"}
                      className={"!px-1 !py-0"}
                      onClick={() => setActiveMoreSocialType({ count: social.count, platform: social.platform })}
                    />
                  </div>
                )}
              </div>
            ) : null;
          })}
        </div>
        <Modal
          onClose={() => setActiveMoreSocialType(null)}
          isOpen={!!activeMoreSocialType}
          content={CompanyDetailMoreData}
          contentProps={{
            linkType: "website",
            featuredIcon: (
              <FeaturedIcon
                icon={
                  <img src={imageUrl(activeMoreSocialType?.platform || "linkedin")!} className={"size-5"} alt={""} />
                }
              />
            ),
            heading: t(`company.detail.more.social.heading`, {
              count: activeMoreSocialType?.count,
              name: capitalizeFirstLetter(activeMoreSocialType?.platform ?? ""),
            }),
            placeholder: t(`company.detail.more.social.placeholder`),
            infiniteQuery: (query) =>
              useCompanyDetailSocials({ id: data?.id, query, platform: activeMoreSocialType?.platform || "linkedin" }),
          }}
        />
      </>
    )
  ) : (
    <div className={"ml-auto flex justify-end gap-2"}>
      <div className={"size-6 animate-pulse rounded-md bg-gray-300"} />
      <div className={"size-6 animate-pulse rounded-md bg-gray-300"} />
    </div>
  );
};

export default CompanyDetailHeader;
